import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import * as galleryApi from './galleryApi';
import type {
  BlobSummary,
  BlobUploadInput,
  GalleryAppsPage,
  GalleryEntry,
  GalleryPage,
  ReportReason,
  StorageUsage,
  SubmissionSummary,
} from './galleryApi';

export const GALLERY_LIST_KEY = ['gallery', 'list'] as const;
export const GALLERY_APPS_KEY = ['gallery', 'apps'] as const;
export const MY_SUBMISSIONS_KEY = ['gallery', 'me', 'submissions'] as const;
export const STORAGE_USAGE_KEY = ['gallery', 'storage-usage'] as const;
export function galleryEntryKey(id: string): readonly unknown[] {
  return ['gallery', 'entry', id] as const;
}

/** Page size for the showcase list. Tuned so a single response packs
 *  enough cards to fill the grid above the fold on a laptop without
 *  blowing past 1MB of JSON for big galleries. */
export const GALLERY_PAGE_SIZE = 24;

export function useGalleryList(
  pageSize: number = GALLERY_PAGE_SIZE,
): UseInfiniteQueryResult<{ pages: GalleryPage[]; pageParams: number[] }, Error> {
  return useInfiniteQuery({
    queryKey: [...GALLERY_LIST_KEY, pageSize] as const,
    queryFn: ({ pageParam }) => galleryApi.listGallery({ limit: pageSize, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.has_more ? last.offset + last.items.length : undefined),
  });
}

export function useGalleryApps(
  pageSize: number = GALLERY_PAGE_SIZE,
): UseInfiniteQueryResult<{ pages: GalleryAppsPage[]; pageParams: number[] }, Error> {
  return useInfiniteQuery({
    queryKey: [...GALLERY_APPS_KEY, pageSize] as const,
    queryFn: ({ pageParam }) => galleryApi.listGalleryApps({ limit: pageSize, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.has_more ? last.offset + last.items.length : undefined),
  });
}

/** Convenience: flatten an infinite-query result into a single list. */
export function flattenInfinite<T>(data: { pages: { items: T[] }[] } | undefined): T[] {
  if (!data) return [];
  return data.pages.flatMap((page) => page.items);
}

export function useUpdateMyDescription(): UseMutationResult<
  GalleryEntry,
  Error,
  { id: string; description: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, description }) => galleryApi.updateMyDescription(id, description),
    onSuccess: (entry) => {
      // Refresh the single-entry cache + invalidate the listing so
      // the new description shows on the showcase grid too.
      qc.setQueryData(galleryEntryKey(entry.id), entry);
      void qc.invalidateQueries({ queryKey: GALLERY_LIST_KEY });
    },
  });
}

export function useGalleryEntry(id: string): UseQueryResult<GalleryEntry, Error> {
  return useQuery({
    queryKey: galleryEntryKey(id),
    queryFn: () => galleryApi.getGalleryEntry(id),
    enabled: !!id,
  });
}

/**
 * The kid's own submissions — any status. The studio shows status
 * badges from this query. We poll while at least one submission is
 * mid-flight (`pending` / `processing`) so the badge flips on its
 * own once moderation finishes.
 */
export function useMySubmissions(options?: {
  enabled?: boolean;
}): UseQueryResult<SubmissionSummary[], Error> {
  return useQuery({
    queryKey: MY_SUBMISSIONS_KEY,
    queryFn: galleryApi.listMySubmissions,
    enabled: options?.enabled ?? true,
    staleTime: 5_000,
    // Polling is dynamic: cheap when everything is settled (returns
    // false to stop), and snappy when the pipeline is working.
    refetchInterval: (query) => {
      const data = query.state.data as SubmissionSummary[] | undefined;
      if (!data) return false;
      const inFlight = data.some((s) => s.status === 'pending' || s.status === 'processing');
      return inFlight ? 2_000 : false;
    },
  });
}

export function useSubmitToGallery(): UseMutationResult<
  SubmissionSummary,
  Error,
  { projectId: string; description?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, description }) => galleryApi.submitToGallery(projectId, description),
    onSuccess: () => {
      // Force a refetch of the kid's submission list so the new
      // pending row shows up before the user has to reload.
      void qc.invalidateQueries({ queryKey: MY_SUBMISSIONS_KEY });
      void qc.invalidateQueries({ queryKey: GALLERY_LIST_KEY });
    },
  });
}

export function useWithdrawSubmission(): UseMutationResult<SubmissionSummary, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => galleryApi.withdrawSubmission(id),
    onSuccess: () => {
      // Invalidate every surface the row appears on: the kid's own
      // status list, the public gallery list (which filters by
      // status=approved server-side, so the row drops out), and any
      // open detail page (it will start returning 404 since the
      // detail endpoint also approved-only-filters).
      void qc.invalidateQueries({ queryKey: MY_SUBMISSIONS_KEY });
      void qc.invalidateQueries({ queryKey: GALLERY_LIST_KEY });
    },
  });
}

export function useUploadBlob(): UseMutationResult<
  BlobSummary,
  Error,
  { projectId: string; input: BlobUploadInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, input }) => galleryApi.uploadBlob(projectId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STORAGE_USAGE_KEY });
    },
  });
}

export function useStorageUsage(options?: {
  enabled?: boolean;
}): UseQueryResult<StorageUsage, Error> {
  return useQuery({
    queryKey: STORAGE_USAGE_KEY,
    queryFn: galleryApi.getStorageUsage,
    enabled: options?.enabled ?? true,
  });
}

interface ReportArgs {
  id: string;
  reason: ReportReason;
  detail?: string;
}

export function useReportGalleryEntry(): UseMutationResult<{ status: string }, Error, ReportArgs> {
  return useMutation({
    mutationFn: ({ id, reason, detail }: ReportArgs) =>
      galleryApi.reportGalleryEntry(id, reason, detail),
  });
}
